/**
 * Input Manager
 * Handles keyboard input and maps to DS buttons
 */

import { DSButton } from './EmulatorCore';

export interface KeyMapping {
  [key: string]: DSButton;
}

export const DEFAULT_KEY_MAPPING: KeyMapping = {
  ArrowUp: DSButton.Up,
  ArrowDown: DSButton.Down,
  ArrowLeft: DSButton.Left,
  ArrowRight: DSButton.Right,
  KeyZ: DSButton.A,
  KeyX: DSButton.B,
  KeyA: DSButton.Y,
  KeyS: DSButton.X,
  Enter: DSButton.Start,
  ShiftLeft: DSButton.Select,
  ShiftRight: DSButton.Select,
  KeyQ: DSButton.L,
  KeyW: DSButton.R,
};

export type ButtonStateCallback = (button: DSButton, pressed: boolean) => void;

export class InputManager {
  private keyMapping: KeyMapping;
  private pressedKeys: Set<string> = new Set();
  private buttonStateCallback: ButtonStateCallback | null = null;
  private isEnabled = false;

  constructor(keyMapping: KeyMapping = DEFAULT_KEY_MAPPING) {
    this.keyMapping = { ...keyMapping };
  }

  /**
   * Initialize input manager and start listening to keyboard events
   */
  initialize(callback: ButtonStateCallback): void {
    this.buttonStateCallback = callback;
    this.enable();
  }

  /**
   * Enable input handling
   */
  enable(): void {
    if (this.isEnabled) {
      return;
    }

    window.addEventListener('keydown', this.handleKeyDown);
    window.addEventListener('keyup', this.handleKeyUp);
    window.addEventListener('blur', this.handleBlur);

    this.isEnabled = true;
    console.log('✓ Input manager enabled');
  }

  /**
   * Disable input handling
   */
  disable(): void {
    if (!this.isEnabled) {
      return;
    }

    window.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('keyup', this.handleKeyUp);
    window.removeEventListener('blur', this.handleBlur);

    // Release all pressed keys
    this.releaseAllKeys();

    this.isEnabled = false;
  }

  /**
   * Handle keydown event
   */
  private handleKeyDown = (event: KeyboardEvent): void => {
    const code = event.code;

    // Ignore if key is already pressed (prevents repeat events)
    if (this.pressedKeys.has(code)) {
      return;
    }

    // Check if this key is mapped to a DS button
    const button = this.keyMapping[code];
    if (button !== undefined) {
      event.preventDefault();
      this.pressedKeys.add(code);

      if (this.buttonStateCallback) {
        this.buttonStateCallback(button, true);
      }
    }
  };

  /**
   * Handle keyup event
   */
  private handleKeyUp = (event: KeyboardEvent): void => {
    const code = event.code;

    if (!this.pressedKeys.has(code)) {
      return;
    }

    const button = this.keyMapping[code];
    if (button !== undefined) {
      event.preventDefault();
      this.pressedKeys.delete(code);

      if (this.buttonStateCallback) {
        this.buttonStateCallback(button, false);
      }
    }
  };

  /**
   * Handle window blur (release all keys when window loses focus)
   */
  private handleBlur = (): void => {
    this.releaseAllKeys();
  };

  /**
   * Release all currently pressed keys
   */
  private releaseAllKeys(): void {
    if (!this.buttonStateCallback) {
      return;
    }

    for (const code of this.pressedKeys) {
      const button = this.keyMapping[code];
      if (button !== undefined) {
        this.buttonStateCallback(button, false);
      }
    }

    this.pressedKeys.clear();
  }

  /**
   * Update key mapping
   */
  setKeyMapping(mapping: KeyMapping): void {
    // Release all keys before changing mapping
    this.releaseAllKeys();
    this.keyMapping = { ...mapping };
  }

  /**
   * Get current key mapping
   */
  getKeyMapping(): KeyMapping {
    return { ...this.keyMapping };
  }

  /**
   * Remap a single key
   */
  remapKey(oldKey: string, newKey: string): void {
    const button = this.keyMapping[oldKey];
    if (button !== undefined) {
      delete this.keyMapping[oldKey];
      this.keyMapping[newKey] = button;
    }
  }

  /**
   * Reset to default key mapping
   */
  resetToDefault(): void {
    this.releaseAllKeys();
    this.keyMapping = { ...DEFAULT_KEY_MAPPING };
  }

  /**
   * Get the key code for a specific button
   */
  getKeyForButton(button: DSButton): string | null {
    for (const [key, mappedButton] of Object.entries(this.keyMapping)) {
      if (mappedButton === button) {
        return key;
      }
    }
    return null;
  }

  /**
   * Get user-friendly key name
   */
  static getKeyName(code: string): string {
    const keyNames: Record<string, string> = {
      ArrowUp: '↑',
      ArrowDown: '↓',
      ArrowLeft: '←',
      ArrowRight: '→',
      Enter: 'Enter',
      ShiftLeft: 'Shift',
      ShiftRight: 'Shift',
      Space: 'Space',
      KeyZ: 'Z',
      KeyX: 'X',
      KeyA: 'A',
      KeyS: 'S',
      KeyQ: 'Q',
      KeyW: 'W',
      KeyE: 'E',
      KeyR: 'R',
    };

    return keyNames[code] || code.replace('Key', '').replace('Digit', '');
  }

  /**
   * Get button name
   */
  static getButtonName(button: DSButton): string {
    const buttonNames: Record<DSButton, string> = {
      [DSButton.A]: 'A',
      [DSButton.B]: 'B',
      [DSButton.X]: 'X',
      [DSButton.Y]: 'Y',
      [DSButton.L]: 'L',
      [DSButton.R]: 'R',
      [DSButton.Start]: 'Start',
      [DSButton.Select]: 'Select',
      [DSButton.Up]: 'D-Pad Up',
      [DSButton.Down]: 'D-Pad Down',
      [DSButton.Left]: 'D-Pad Left',
      [DSButton.Right]: 'D-Pad Right',
    };

    return buttonNames[button] || `Button ${button}`;
  }

  /**
   * Check if a specific button is currently pressed
   */
  isButtonPressed(button: DSButton): boolean {
    for (const code of this.pressedKeys) {
      if (this.keyMapping[code] === button) {
        return true;
      }
    }
    return false;
  }

  /**
   * Get all currently pressed buttons
   */
  getPressedButtons(): DSButton[] {
    const buttons: DSButton[] = [];
    for (const code of this.pressedKeys) {
      const button = this.keyMapping[code];
      if (button !== undefined && !buttons.includes(button)) {
        buttons.push(button);
      }
    }
    return buttons;
  }

  /**
   * Cleanup
   */
  destroy(): void {
    this.disable();
    this.buttonStateCallback = null;
  }
}

/**
 * Local storage keys for saving/loading key mappings
 */
export const KEY_MAPPING_STORAGE_KEY = 'ds_emulator_key_mapping';

/**
 * Load key mapping from local storage
 */
export function loadKeyMapping(): KeyMapping {
  try {
    const stored = localStorage.getItem(KEY_MAPPING_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Failed to load key mapping:', error);
  }
  return { ...DEFAULT_KEY_MAPPING };
}

/**
 * Save key mapping to local storage
 */
export function saveKeyMapping(mapping: KeyMapping): void {
  try {
    localStorage.setItem(KEY_MAPPING_STORAGE_KEY, JSON.stringify(mapping));
  } catch (error) {
    console.error('Failed to save key mapping:', error);
  }
}
